// File: src/components/registration/NfpRegistration/Step2Entity.jsx
import React, { useState, useEffect } from 'react';
import { useNfpReg } from '../../../contexts/NfpRegContext';
import { nfpApi } from '../../../services/nfpApi';

const TAX_CLASSIFICATIONS = [
  '501(c)(3) Public Charity',
  '501(c)(3) Private Foundation',
  '501(c)(4) Social Welfare Organization',
  '501(c)(6) Trade / Business Association',
  'Other 501(c)',
  'Government Unit (170(c)(1))',
  'Not Sure',
];

const STATUS_RECOGNITION_OPTIONS = [
  { value: 'irs_determination_letter', label: 'IRS determination letter' },
  { value: 'parent_group_exemption', label: 'Covered under a parent organization’s group exemption / group ruling' },
  { value: 'religious_no_separate_letter', label: 'Church or religious organization without a separate IRS determination letter' },
  { value: 'government_unit_public_entity', label: 'Government unit / public entity' },
  { value: 'other_not_sure', label: 'Other / Not sure' },
];

const MISSION_CATEGORIES = [
  'Education','Health','Food Security','Environment','Youth','Arts & Culture',
  'Faith-Based','Veterans','Animal Welfare','Community Development','Homelessness',
  'Disaster Relief','LGBTQ+','Seniors','International'
];

const PEACH = '#EBA477';
const EIN_RE = /^\d{2}-?\d{7}$/;
const GEN_RE = /^\d{4}$/;

const SUPPORTER_RANGES = {
  emailList: ['0-250', '251-1k', '1k-5k', '5k-25k', '25k-100k', '100k+', 'Unknown'],
  activeDonors: ['0', '1-50', '51-250', '251-1k', '1k-5k', '5k+', 'Unknown'],
  totalDonations: ['$0', '<$25k', '$25-100k', '$100-500k', '$500k-$2M', '$2M+', 'Unknown'],
  volunteers: ['0', '1-25', '26-100', '101-500', '500+', 'Unknown'],
  socialReach: ['0-500', '501-5k', '5k-25k', '25k-100k', '100k+', 'Unknown'],
  crmTools: [
    'Salesforce NPSP',
    'Bloomerang',
    'Neon One',
    'DonorPerfect',
    'Kindful',
    'Givebutter',
    'HubSpot',
    'Mailchimp',
    'Other',
    'None',
  ],
};

const formatEinInput = (value) => {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

function mapSupporterSnapshotToApi(snapshot = {}) {
  const emailListSizeMap = {
    '0-250': '0_250',
    '251-1k': '251_1000',
    '1k-5k': '1k_5k',
    '5k-25k': '5k_25k',
    '25k-100k': '25k_100k',
    '100k+': '100k_plus',
    Unknown: 'unknown',
  };
  const activeDonorsMap = {
    '0': '0',
    '1-50': '1_50',
    '51-250': '51_250',
    '251-1k': '251_1k',
    '1k-5k': '1k_5k',
    '5k+': '5k_plus',
    Unknown: 'unknown',
  };
  const totalDonationsMap = {
    '$0': '0',
    '<$25k': 'lt_25k',
    '$25-100k': '25k_100k',
    '$100-500k': '100k_500k',
    '$500k-$2M': '500k_2m',
    '$2M+': 'gt_2m',
    Unknown: 'unknown',
  };
  const volunteersMap = {
    '0': '0',
    '1-25': '1_25',
    '26-100': '26_100',
    '101-500': '101_500',
    '500+': '500_plus',
    Unknown: 'unknown',
  };
  const socialReachMap = {
    '0-500': '0_500',
    '501-5k': '501_5k',
    '5k-25k': '5k_25k',
    '25k-100k': '25k_100k',
    '100k+': '100k_plus',
    Unknown: 'unknown',
  };
  const crmToolMap = {
    'Salesforce NPSP': 'salesforce_npsp',
    Bloomerang: 'bloomerang',
    'Neon One': 'neon',
    DonorPerfect: 'donorperfect',
    Kindful: 'kindful',
    Givebutter: 'givebutter',
    HubSpot: 'hubspot',
    Mailchimp: 'mailchimp',
    Other: 'other',
    None: 'none',
  };

  return {
    asOf: snapshot.asOf || new Date().toISOString().slice(0, 10),
    isEstimate: !!snapshot.isEstimate,
    emailContactsRange: emailListSizeMap[snapshot.emailListSize] || 'unknown',
    activeDonorsL12MRange: activeDonorsMap[snapshot.activeDonors] || 'unknown',
    totalDonationsL12MRange: totalDonationsMap[snapshot.totalDonations] || 'unknown',
    volunteersL12MRange: volunteersMap[snapshot.volunteers] || 'unknown',
    socialReachRange: socialReachMap[snapshot.socialReach] || 'unknown',
    crmProvider: crmToolMap[snapshot.crmTool] || 'none',
  };
}

function mapRecognitionTypeToProofType(choice, isCoveredByParentGroupExemption) {
  switch (choice) {
    case 'irs_determination_letter':
      return 'lod';
    case 'parent_group_exemption':
      return 'operating_under_gen';
    case 'religious_no_separate_letter':
      return isCoveredByParentGroupExemption === 'yes' ? 'operating_under_gen' : 'other_supporting';
    case 'government_unit_public_entity':
      return 'government_unit';
    case 'other_not_sure':
      return 'other_supporting';
    default:
      return undefined;
  }
}

export default function Step2Entity({ onBack, onNext }) {
  const { draft, setDraft, registration, setRegistration } = useNfpReg();

  const [legalName, setLegalName] = useState(draft?.entity?.legalName ?? '');
  const [publicName, setPublicName] = useState(draft?.entity?.publicName ?? '');
  const [structure, setStructure] = useState(draft?.entity?.structure ?? 'Independent');
  const [hasOwnEin, setHasOwnEin] = useState(draft?.entity?.hasOwnEin ?? 'yes');

  const [ein, setEin] = useState(draft?.entity?.ein ?? '');
  const [parentEin, setParentEin] = useState(draft?.entity?.parentEin ?? '');
  const [parentOrganizationName, setParentOrganizationName] = useState(draft?.entity?.parentOrganizationName ?? '');
  const [gen, setGen] = useState(draft?.entity?.gen ?? '');
  const [chapterUnitId, setChapterUnitId] = useState(draft?.entity?.chapterUnitId ?? '');

  const [taxClassification, setTaxClassification] = useState(draft?.entity?.taxClassification ?? '');
  const [missionCategory, setMissionCategory] = useState(draft?.entity?.missionCategories?.[0] ?? '');
  const [recognitionType, setRecognitionType] = useState(draft?.entity?.recognitionType ?? '');
  const [isRecognizedByParent, setIsRecognizedByParent] = useState(draft?.entity?.isRecognizedByParent ?? '');
  const [isCoveredByParentGroupExemption, setIsCoveredByParentGroupExemption] = useState(
    draft?.entity?.isCoveredByParentGroupExemption ?? ''
  );
  const [governmentEntityName, setGovernmentEntityName] = useState(draft?.entity?.governmentEntityName ?? '');
  const [governmentDivision, setGovernmentDivision] = useState(draft?.entity?.governmentDivision ?? '');
  const [recognitionNotes, setRecognitionNotes] = useState(draft?.entity?.recognitionNotes ?? '');
  const [supporterSnapshot, setSupporterSnapshot] = useState(() => {
    const snap = draft?.entity?.supporterSnapshot || {};
    return {
      asOf: snap.asOf || new Date().toISOString().slice(0, 10),
      isEstimate: snap.isEstimate ?? true,
      emailListSize: snap.emailListSize || SUPPORTER_RANGES.emailList[0],
      activeDonors: snap.activeDonors || SUPPORTER_RANGES.activeDonors[0],
      totalDonations: snap.totalDonations || SUPPORTER_RANGES.totalDonations[0],
      volunteers: snap.volunteers || SUPPORTER_RANGES.volunteers[0],
      socialReach: snap.socialReach || SUPPORTER_RANGES.socialReach[0],
      crmTool: snap.crmTool || 'None',
    };
  });

  const [err, setErr] = useState('');

  // In case supporterSnapshot in draft changes under us
  useEffect(() => {
    if (draft?.entity?.supporterSnapshot) {
      const snap = draft.entity.supporterSnapshot;
      setSupporterSnapshot((prev) => ({
        ...prev,
        ...snap,
        asOf: snap.asOf || prev.asOf,
      }));
    }
  }, [draft?.entity?.supporterSnapshot]);

  const updateSnapshot = (patch) => {
    setSupporterSnapshot((prev) => ({ ...prev, ...patch }));
  };

  useEffect(() => {
    if (recognitionType !== 'parent_group_exemption') {
      setIsRecognizedByParent('');
    }
    if (recognitionType !== 'religious_no_separate_letter') {
      setIsCoveredByParentGroupExemption('');
    }
    if (!['parent_group_exemption', 'religious_no_separate_letter'].includes(recognitionType)) {
      setParentOrganizationName('');
      setGen('');
      setChapterUnitId('');
    }
    if (recognitionType !== 'government_unit_public_entity') {
      setGovernmentEntityName('');
      setGovernmentDivision('');
    }
    if (recognitionType !== 'other_not_sure') {
      setRecognitionNotes('');
    }
  }, [recognitionType]);

  const validate = () => {
    if (!legalName.trim()) return 'Legal entity name is required.';
    if (!structure) return 'Organization Type is required.';
    if (!taxClassification) return 'Tax classification is required.';
    if (!missionCategory) return 'Mission category is required.';
    if (!recognitionType) return 'Status recognition is required.';

    if (structure === 'Independent' || structure === 'Umbrella/Parent') {
      if (!EIN_RE.test(ein)) return 'EIN is required for Independent orgs (format: 12-3456789).';
    }

    if (structure === 'Chapter/Affiliate') {
      if (hasOwnEin === 'yes') {
        if (!EIN_RE.test(ein)) return 'Local EIN is required for a Chapter/Affiliate with its own EIN.';
      } else {
        if (!EIN_RE.test(parentEin)) return 'Parent EIN is required when Chapter/Affiliate has no local EIN.';
      }
    }

    if (structure === 'Religious / Faith-Based Organization') {
      if (hasOwnEin === 'yes') {
        if (!EIN_RE.test(ein)) return 'EIN is required for a religious or faith-based organization with its own EIN.';
      } else {
        if (!EIN_RE.test(parentEin)) return 'EIN is required when this local unit does not have its own EIN.';
      }
    }

    if (structure === 'Education / Health / Government Affiliated') {
      if (hasOwnEin === 'yes') {
        if (!EIN_RE.test(ein)) return 'EIN is required for an education, health, or government affiliated organization with its own EIN.';
      } else {
        if (!EIN_RE.test(parentEin)) return 'EIN is required when this local unit does not have its own EIN.';
      }
    }

    if (recognitionType === 'parent_group_exemption') {
      if (!parentOrganizationName.trim()) return 'Parent Organization Name is required.';
      if (!isRecognizedByParent) return 'Please tell us whether this chapter or unit is recognized by the parent organization.';
      if (gen.trim() && !GEN_RE.test(String(gen || '').trim())) {
        return 'Group Exemption Number (GEN) must be a 4-digit number.';
      }
    }

    if (recognitionType === 'religious_no_separate_letter') {
      if (!isCoveredByParentGroupExemption) {
        return 'Please tell us whether this organization is covered under a parent organization’s group exemption.';
      }
      if (isCoveredByParentGroupExemption === 'yes') {
        if (!parentOrganizationName.trim()) return 'Parent Organization Name is required.';
        if (gen.trim() && !GEN_RE.test(String(gen || '').trim())) {
          return 'Group Exemption Number (GEN) must be a 4-digit number.';
        }
      }
    }

    if (recognitionType === 'government_unit_public_entity' && !governmentEntityName.trim()) {
      return 'Government Entity Name is required.';
    }

    if (recognitionType === 'other_not_sure' && !recognitionNotes.trim()) {
      return 'Please describe this organization’s tax status or affiliation.';
    }

    return null;
  };

  const handleNext = async () => {
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setErr('');

    const sanitizedSnapshot = {
      asOf: supporterSnapshot.asOf || new Date().toISOString().slice(0, 10),
      isEstimate: !!supporterSnapshot.isEstimate,
      emailListSize: supporterSnapshot.emailListSize,
      activeDonors: supporterSnapshot.activeDonors,
      totalDonations: supporterSnapshot.totalDonations,
      volunteers: supporterSnapshot.volunteers,
      socialReach: supporterSnapshot.socialReach,
      crmTool: supporterSnapshot.crmTool,
    };

    // Update local draft (no payoutIntent fields anymore)
    setDraft((prev) => ({
      ...prev,
      entity: {
        legalName,
        publicName: publicName.trim(),
        structure,
        hasOwnEin,
        ein: ein.trim(),
        parentEin: parentEin.trim(),
        parentOrganizationName: parentOrganizationName.trim(),
        gen: gen.trim(),
        chapterUnitId: chapterUnitId.trim(),
        taxClassification,
        missionCategories: missionCategory ? [missionCategory] : [],
        recognitionType,
        isRecognizedByParent,
        isCoveredByParentGroupExemption,
        governmentEntityName: governmentEntityName.trim(),
        governmentDivision: governmentDivision.trim(),
        recognitionNotes: recognitionNotes.trim(),
        supporterSnapshot: sanitizedSnapshot,
      },
    }));

    if (registration?._id) {
      try {
        const payload = {
          legalName: legalName.trim(),
          publicNameDraft:
            (publicName && publicName.trim() && publicName.trim() !== legalName.trim())
              ? publicName.trim()
              : undefined, // omit if empty/same as legal
          taxClassification,
          missionCategories: missionCategory ? [missionCategory] : [],
          orgStructure: normalizeStructure(structure),
          hasLocalEIN: hasOwnEin === 'yes',
          ein: ein.trim() || undefined,
          parent: {
            name: parentOrganizationName.trim() || undefined,
            ein: parentEin.trim() || undefined,
            gen: gen.trim() || undefined,
            chapterUnitId: chapterUnitId.trim() || undefined,
          },
          statusRecognition: {
            type: recognitionType || undefined,
            isRecognizedByParent: isRecognizedByParent || undefined,
            isCoveredByParentGroupExemption: isCoveredByParentGroupExemption || undefined,
            governmentEntityName: governmentEntityName.trim() || undefined,
            governmentDivision: governmentDivision.trim() || undefined,
            notes: recognitionNotes.trim() || undefined,
          },
          scope: inferScope(structure),
          // SAFE default: RYPIL pays the registered org; no user-entered split.
          initialPayoutIntent: { mode: 'local', split: null },
        };

        const resp = await nfpApi.putEntity(registration._id, payload);
        if (resp?.registration) {
          setRegistration(resp.registration);
        }

        if (sanitizedSnapshot) {
          const supporterResp = await nfpApi.putSupporterSnapshot(
            registration._id,
            mapSupporterSnapshotToApi(sanitizedSnapshot)
          );
          if (supporterResp?.registration) {
            setRegistration(supporterResp.registration);
          }
        }

        const proofType = mapRecognitionTypeToProofType(
          recognitionType,
          isCoveredByParentGroupExemption
        );
        if (proofType) {
          const proofResp = await nfpApi.putProof(registration._id, { type: proofType });
          if (proofResp?.registration) {
            setRegistration(proofResp.registration);
          }
        }
      } catch (apiErr) {
        if (apiErr?.status === 409) {
          setErr(apiErr?.message || 'This EIN is already claimed by another organization.');
        } else {
          setErr(apiErr?.message || 'Failed to save entity information.');
        }
        return;
      }
    }

    onNext();
  };

  const showHasOwnEin =
    structure === 'Chapter/Affiliate' ||
    structure === 'Religious / Faith-Based Organization' ||
    structure === 'Education / Health / Government Affiliated';

  const showParentGroupFields = recognitionType === 'parent_group_exemption';
  const showReligiousRecognitionFields = recognitionType === 'religious_no_separate_letter';
  const showReligiousParentFields =
    recognitionType === 'religious_no_separate_letter' &&
    isCoveredByParentGroupExemption === 'yes';
  const showGovernmentRecognitionFields = recognitionType === 'government_unit_public_entity';
  const showOtherRecognitionFields = recognitionType === 'other_not_sure';

  return (
    <div style={styles.stack}>
      {err && <div style={styles.error}>{err}</div>}

      {/* A) Identity */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>A) Identity</h3>

        <div>
          <label>Legal entity name</label>
          <input value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
        </div>
        <div>
          <label>
            Public-facing name
          </label>
          <input
            placeholder="Community name donors recognize"
            value={publicName}
            onChange={(e) => setPublicName(e.target.value)}
          />
        </div>

        <div style={styles.gridTwo}>
          <div>
            <label>Organization Type</label>
            <select value={structure} onChange={(e) => setStructure(e.target.value)}>
              <option>Independent</option>
              <option>Chapter/Affiliate</option>
              <option>Umbrella/Parent</option>
              <option>Religious / Faith-Based Organization</option>
              <option>Education / Health / Government Affiliated</option>
              <option>509(a)(3) Supporting Org</option>
            </select>
          </div>

          {showHasOwnEin && (
            <div>
              <label>Does this unit have its own EIN?</label>
              <select value={hasOwnEin} onChange={(e) => setHasOwnEin(e.target.value)}>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          )}
        </div>

        {structure === 'Independent' && (
          <div>
            <label>EIN</label>
            <input
              placeholder="12-3456789"
              pattern="\\d{2}-\\d{7}"
              value={ein}
              onChange={(e) => setEin(formatEinInput(e.target.value))}
            />
          </div>
        )}

        {structure === 'Umbrella/Parent' && (
          <div>
            <label>EIN</label>
            <input
              placeholder="12-3456789"
              pattern="\\d{2}-\\d{7}"
              value={ein}
              onChange={(e) => setEin(formatEinInput(e.target.value))}
            />
          </div>
        )}

        {structure === 'Chapter/Affiliate' && (
          <div style={styles.gridTwo}>
            {hasOwnEin === 'yes' ? (
              <div>
                <label>EIN</label>
                <input
                  placeholder="12-3456789"
                  pattern="\\d{2}-\\d{7}"
                  value={ein}
                  onChange={(e) => setEin(formatEinInput(e.target.value))}
                />
              </div>
            ) : (
              <>
                <div>
                  <label>EIN</label>
                  <input
                    placeholder="12-3456789"
                    pattern="\\d{2}-\\d{7}"
                    value={parentEin}
                    onChange={(e) => setParentEin(formatEinInput(e.target.value))}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {structure === 'Religious / Faith-Based Organization' && (
          <div style={styles.gridTwo}>
            {hasOwnEin === 'yes' ? (
              <div>
                <label>EIN</label>
                <input
                  placeholder="12-3456789"
                  pattern="\\d{2}-\\d{7}"
                  value={ein}
                  onChange={(e) => setEin(formatEinInput(e.target.value))}
                />
              </div>
            ) : (
              <div>
                <label>EIN</label>
                <input
                  placeholder="12-3456789"
                  pattern="\\d{2}-\\d{7}"
                  value={parentEin}
                  onChange={(e) => setParentEin(formatEinInput(e.target.value))}
                />
              </div>
            )}
          </div>
        )}

        {structure === 'Education / Health / Government Affiliated' && (
          <div style={styles.gridTwo}>
            {hasOwnEin === 'yes' ? (
              <div>
                <label>EIN</label>
                <input
                  placeholder="12-3456789"
                  pattern="\\d{2}-\\d{7}"
                  value={ein}
                  onChange={(e) => setEin(formatEinInput(e.target.value))}
                />
              </div>
            ) : (
              <div>
                <label>EIN</label>
                <input
                  placeholder="12-3456789"
                  pattern="\\d{2}-\\d{7}"
                  value={parentEin}
                  onChange={(e) => setParentEin(formatEinInput(e.target.value))}
                />
              </div>
            )}
          </div>
        )}
      </section>

      {/* B) Classification & Mission */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>B) Classification &amp; Mission</h3>

        <div>
          <label>Tax classification</label>
          <p style={styles.hint}>Select the organization’s primary tax classification.</p>
          <select value={taxClassification} onChange={(e) => setTaxClassification(e.target.value)}>
            <option value="">Select one…</option>
            {TAX_CLASSIFICATIONS.map((tc) => (
              <option key={tc} value={tc}>
                {tc}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Mission category</label>
          <select value={missionCategory} onChange={(e) => setMissionCategory(e.target.value)} required>
            <option value="">Select</option>
            {MISSION_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* C) Proof & Compliance */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>C) Status Recognition</h3>
        <div>
          <label>How is this status documented or recognized?</label>
          <p style={styles.hint}>
            Tell us how this organization’s tax status is documented or recognized. We’ll collect supporting proof later in the registration process.
          </p>
          <select value={recognitionType} onChange={(e) => setRecognitionType(e.target.value)}>
            <option value="">Select one…</option>
            {STATUS_RECOGNITION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {showParentGroupFields && (
          <>
            <div>
              <label>Parent Organization Name</label>
              <p style={styles.hint}>
                Enter the name of the parent or central organization that holds the group exemption.
              </p>
              <input value={parentOrganizationName} onChange={(e) => setParentOrganizationName(e.target.value)} />
            </div>
            <div style={styles.gridTwo}>
              <div>
                <label>Group Exemption Number (GEN)</label>
                <div style={{ opacity: 0.75, fontSize: 13, marginBottom: 6 }}>
                  Enter the 4-digit IRS group exemption number, if applicable.
                </div>
                <input
                  placeholder="1234"
                  inputMode="numeric"
                  maxLength={4}
                  value={gen}
                  onChange={(e) => setGen(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
              <div>
                <label>Is this chapter or unit recognized by the parent organization?</label>
                <p style={styles.hint}>
                  We need to understand whether this local unit is formally recognized or covered by the parent organization.
                </p>
                <select value={isRecognizedByParent} onChange={(e) => setIsRecognizedByParent(e.target.value)}>
                  <option value="">Select one…</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="not_sure">Not sure</option>
                </select>
              </div>
            </div>
            <div>
              <label>Chapter / Unit ID (optional)</label>
              <p style={styles.hint}>Enter your chapter, parish, campus, or unit ID if one exists.</p>
              <input value={chapterUnitId} onChange={(e) => setChapterUnitId(e.target.value)} />
            </div>
          </>
        )}

        {showReligiousRecognitionFields && (
          <>
            <div>
              <label>Is this organization covered under a parent organization’s group exemption?</label>
              <select
                value={isCoveredByParentGroupExemption}
                onChange={(e) => setIsCoveredByParentGroupExemption(e.target.value)}
              >
                <option value="">Select one…</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {showReligiousParentFields && (
              <>
                <div>
                  <label>Parent Organization Name</label>
                  <p style={styles.hint}>
                    Enter the name of the parent or central religious organization, if applicable.
                  </p>
                  <input value={parentOrganizationName} onChange={(e) => setParentOrganizationName(e.target.value)} />
                </div>
                <div style={styles.gridTwo}>
                  <div>
                    <label>Group Exemption Number (GEN)</label>
                    <div style={{ opacity: 0.75, fontSize: 13, marginBottom: 6 }}>
                      Enter the 4-digit IRS group exemption number, if applicable.
                    </div>
                    <input
                      placeholder="1234"
                      inputMode="numeric"
                      maxLength={4}
                      value={gen}
                      onChange={(e) => setGen(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    />
                  </div>
                  <div>
                    <label>Chapter / Unit ID (optional)</label>
                    <p style={styles.hint}>Enter your parish, diocese, chapter, or unit ID if one exists.</p>
                    <input value={chapterUnitId} onChange={(e) => setChapterUnitId(e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {showGovernmentRecognitionFields && (
          <div style={styles.gridTwo}>
            <div>
              <label>Government Entity Name</label>
              <p style={styles.hint}>Enter the full name of the government entity.</p>
              <input value={governmentEntityName} onChange={(e) => setGovernmentEntityName(e.target.value)} />
            </div>
            <div>
              <label>Department / Division (optional)</label>
              <p style={styles.hint}>
                Enter the department, division, school, park district, or agency name if applicable.
              </p>
              <input value={governmentDivision} onChange={(e) => setGovernmentDivision(e.target.value)} />
            </div>
          </div>
        )}

        {showOtherRecognitionFields && (
          <div>
            <label>Please describe</label>
            <p style={styles.hint}>
              Tell us anything that may help us understand the organization’s tax status or affiliation.
            </p>
            <textarea
              value={recognitionNotes}
              onChange={(e) => setRecognitionNotes(e.target.value)}
              rows={4}
              style={styles.textarea}
            />
          </div>
        )}
      </section>

      {/* D) Supporter Snapshot */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>D) Supporter Snapshot</h3>
        <p style={styles.snapshotIntro}>
          Sharing a snapshot lets us tailor onboarding, digital assets, and dashboards to your current reach.
          That way you grow supporters faster, unlock more revenue, and maximize <span className="rypil-word">rypil</span>’s impact on your mission.
        </p>
        <div style={styles.gridFour}>
          <div>
            <label>As of date</label>
            <input
              type="date"
              value={supporterSnapshot.asOf}
              onChange={(e) => updateSnapshot({ asOf: e.target.value })}
            />
          </div>
          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={supporterSnapshot.isEstimate}
              onChange={(e) => updateSnapshot({ isEstimate: e.target.checked })}
              style={styles.checkbox}
            />
            <span>This is an estimate</span>
          </label>
        </div>

        <div style={styles.gridFour}>
          <div>
            <label>Supporter list size</label>
            <select
              value={supporterSnapshot.emailListSize}
              onChange={(e) => updateSnapshot({ emailListSize: e.target.value })}
            >
              {SUPPORTER_RANGES.emailList.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Active donors (L12M)</label>
            <select
              value={supporterSnapshot.activeDonors}
              onChange={(e) => updateSnapshot({ activeDonors: e.target.value })}
            >
              {SUPPORTER_RANGES.activeDonors.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Total donations (L12M)</label>
            <select
              value={supporterSnapshot.totalDonations}
              onChange={(e) => updateSnapshot({ totalDonations: e.target.value })}
            >
              {SUPPORTER_RANGES.totalDonations.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Volunteers (L12M)</label>
            <select
              value={supporterSnapshot.volunteers}
              onChange={(e) => updateSnapshot({ volunteers: e.target.value })}
            >
              {SUPPORTER_RANGES.volunteers.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={styles.gridThree}>
          <div>
            <label>Social reach</label>
            <select
              value={supporterSnapshot.socialReach}
              onChange={(e) => updateSnapshot({ socialReach: e.target.value })}
            >
              {SUPPORTER_RANGES.socialReach.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>CRM / Email tool</label>
            <select
              value={supporterSnapshot.crmTool}
              onChange={(e) => updateSnapshot({ crmTool: e.target.value })}
            >
              {SUPPORTER_RANGES.crmTools.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <div style={styles.actions}>
        <button type="button" className="secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="primary" onClick={handleNext}>
          Continue
        </button>
      </div>
    </div>
  );
}

function normalizeStructure(label) {
  const map = {
    independent: 'independent',
    'chapter/affiliate': 'chapter_affiliate',
    chapter_affiliate: 'chapter_affiliate',
    umbrella_parent: 'umbrella_parent',
    'umbrella/parent': 'umbrella_parent',
    '509(a)(3) supporting org': 'supporting_509a3',
    supporting_509a3: 'supporting_509a3',
    'religious / faith-based organization': 'religious_gen',
    religious_gen: 'religious_gen',
    'education / health / government affiliated': 'edu_health_gov',
    edu_health_gov: 'edu_health_gov',
  };
  const key = (label || '').toLowerCase();
  return map[key] || 'independent';
}

function inferScope(structureLabel) {
  const normalized = normalizeStructure(structureLabel);
  if (normalized === 'umbrella_parent') return 'parent_network';
  if (normalized === 'chapter_affiliate') return 'multi_chapter';
  return 'single_unit';
}

const styles = {
  stack: {
    display: 'grid',
    gap: 28,
  },
  section: {
    display: 'grid',
    gap: 10,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: '0 0 4px',
  },
  gridTwo: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  },
  gridThree: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  },
  gridFour: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  hint: {
    fontSize: 12,
    opacity: 0.7,
    marginTop: 6,
  },
  textarea: {
    width: '100%',
    minHeight: 110,
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(255,255,255,0.12)',
    color: 'white',
    fontSize: 16,
    padding: '12px 14px',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
  },
  error: {
    background: 'rgba(248,113,113,0.16)',
    border: '1px solid rgba(248,113,113,0.45)',
    color: '#FCA5A5',
    padding: '10px 14px',
    borderRadius: 14,
    fontSize: 13,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
  },
  snapshotIntro: {
    fontSize: 14,
    lineHeight: 1.5,
    opacity: 0.82,
    margin: '0 0 8px',
  },
  checkboxRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    fontSize: 13,
  },
  checkbox: {
    width: 18,
    height: 18,
    accentColor: PEACH,
  },
};
